// ---------------------------------------------------------------------------
// Report types — mirror the ReportCardReport::Historical and
// ReportCardReport::Comparison renderer output shapes (Ruby side).
// ---------------------------------------------------------------------------

import {
	getRequestAccountId,
	notifyAccountMismatch,
} from '$lib/current-account';

export type ReportCardSection = {
	id: number;
	name: string;
	grade: string | null;
	score: string | null;
	description: string | null;
	display_order: number;
};

export type ReportCardSections = {
	average: { grade: string | null; score: string | null };
	items: ReportCardSection[];
};

export type ReportCardPeriod = {
	reference_date: string;
	report_card_uuid: string | null;
	sections: ReportCardSections;
	metadata?: { no_data_error?: unknown };
};

export type HistoricalReportCardReport = {
	account_id: number;
	report_card_type: string;
	report_card_factory_name?: string;
	reference_date: string;
	periodicity: string;
	window_periods: number;
	facets: Record<string, unknown>;
	periods: ReportCardPeriod[];
	stages?: ReportStage[];
	/** Set by the server when served via the DataTurk staff subscription bypass. */
	dt_unlocked?: boolean;
};

export type ReportCardAccountEntry = {
	account_id: number;
	account_slug: string;
	account_name: string;
	report_card_uuid: string | null;
	sections: ReportCardSections;
	metadata?: { no_data_error?: unknown };
};

export type ComparisonReportCardReport = {
	account_ids: number[];
	report_card_type: string;
	report_card_factory_name?: string;
	reference_date: string;
	periodicity: string;
	facets: Record<string, unknown>;
	accounts: ReportCardAccountEntry[];
	stages?: ReportStage[];
	/** Set by the server when served via the DataTurk staff subscription bypass. */
	dt_unlocked?: boolean;
};

/** A table-shaped report (e.g. a metric broken down by hour/day) — no report card involved. */
export type SheetReport = {
	account_id: number;
	reference_date: string;
	name?: string;
	/** Named tables composed by the renderer — tile config picks which one each tile reads via `table`. */
	tables: Record<string, Record<string, string | number | null>[]>;
	stages?: ReportStage[];
	/** Present only for reports with a user-selectable metric dropdown (e.g. key-metrics-predictions). */
	metric_factory_generator?: string;
	metric_factory_options?: { generator: string; name: string; format: string | null }[];
	/** Present only for reports with a client-side view toggle (e.g. business-revenue-breakdown).
	    Each mode maps a tile's base table name to a `_suffix` variant already present in `tables`. */
	view_modes?: ReportViewMode[];
	/** Present only for reports with a period-window dropdown (business-revenue-breakdown). */
	period?: string;
	period_options?: { key: string; label: string }[];
	/** Set by the server when served via the DataTurk staff subscription bypass. */
	dt_unlocked?: boolean;
};

/** One option in a report's view toggle — swaps mode-variant tile tables client-side, no re-fetch. */
export type ReportViewMode = {
	key: string;
	label: string;
	suffix: string;
};

export type ReportCardReport =
	| HistoricalReportCardReport
	| ComparisonReportCardReport
	| SheetReport
	| { no_data: true; name?: string; reference_date?: string; dt_unlocked?: boolean }
	| { authorized?: boolean; factory_name?: string; dt_unlocked?: boolean };

// ---------------------------------------------------------------------------
// Report layout types — driven by report_factories.stages JSONB
// ---------------------------------------------------------------------------

export type ReportTileConfig = {
	type: string;
	span?: number;
	repeat?: string;
	/** Caption shown above the tile's content — most useful when a stage has more than one chart. */
	description?: string;
	/** Which named table (from the report's `tables`) this tile reads — 'sheet' and 'chart' tiles only. */
	table?: string;
	/** Ordered, labeled allow-list of columns to display — 'sheet' tiles only. Keys not listed are hidden. */
	columns?: { key: string; label: string; format?: string }[];
	/** Which fields to plot — 'chart' tiles only. */
	chart?: { type: 'bar' | 'line' | 'pie' | 'area'; x: string; y: string; x_format?: string; y_label?: string };
	/** A scalar caption shown in the middle of the ring — 'flow' tiles only. */
	center?: { table: string; field: string; format?: string; label?: string };
	/** A scalar caption shown above the chart — 'funnel' tiles only. */
	title?: { table: string; field: string; format?: string; label?: string };
};

export type ReportStageUnavailable = {
	reason: string;
	unlock_plan?: string;
};

export type ReportStage = {
	name?: string;
	tiles?: ReportTileConfig[];
	unavailable?: ReportStageUnavailable;
};

/**
 * Typed API error carrying HTTP status for caller handling.
 */
export class ApiError extends Error {
	constructor(
		public readonly status: number,
		public readonly url: string,
		public readonly body?: Record<string, unknown>,
	) {
		super(`API ${status}: ${url}`);
		this.name = 'ApiError';
	}
}

/**
 * Fetch wrapper with Rails CSRF token injection.
 * Uses the Devise session cookie automatically (same-origin request).
 *
 * Throws ApiError on non-2xx responses so callers can distinguish
 * 401 (unauthenticated) from 403 (forbidden) from other errors.
 * The parsed JSON body (if any) is attached to the error as `body`.
 */
export async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
	const csrf =
		document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
			?.content ?? '';

	// Declare the account the client believes it is viewing on every customer request, so
	// the server can assert it and refuse a silent cross-tenant serve.
	const accountId = getRequestAccountId();
	const accountHeader: Record<string, string> =
		accountId != null && url.startsWith('/api/customer/')
			? { 'X-Account-Id': String(accountId) }
			: {};

	const res = await fetch(url, {
		...opts,
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			'X-CSRF-Token': csrf,
			...accountHeader,
			...opts?.headers,
		},
	});

	if (!res.ok) {
		let body: Record<string, unknown> | undefined;
		try {
			body = await res.json();
		} catch {
			/* not JSON */
		}
		if (res.status === 403 && body?.error === 'account_mismatch') {
			notifyAccountMismatch();
		}
		throw new ApiError(res.status, url, body);
	}

	// Many admin mutation endpoints return 204 No Content.
	if (res.status === 204) {
		return undefined as T;
	}

	// Some test doubles only provide json(); support both response shapes.
	if (typeof res.text === 'function') {
		const text = await res.text();
		if (!text) {
			return undefined as T;
		}

		return JSON.parse(text) as T;
	}

	if (typeof res.json === 'function') {
		return (await res.json()) as T;
	}

	return undefined as T;
}

/**
 * Fetch a rendered report by factory slug.
 * Passes optional coordinates as query params (reference_date, periodicity, etc.).
 */
export function fetchReport(
	slug: string,
	params?: Record<string, string>,
): Promise<ReportCardReport> {
	const qs =
		params && Object.keys(params).length
			? '?' + new URLSearchParams(params).toString()
			: '';
	return apiFetch<ReportCardReport>(`/api/customer/reports/${slug}${qs}`);
}

// ---------------------------------------------------------------------------
// ReportCard content types — the actual report card with groups, sections,
// summaries, details, and improvement suggestions.
// ---------------------------------------------------------------------------

export type ReportCardSectionItem = {
	name: string;
	grade: string;
	score: number;
	units: string;
	weight: string;
	metric_value: number;
	display_order: number;
	directionality: string;
	how_to_improve: {
		delta_units?: { basic: string; specialized: string };
		qualitative?: { basic: string; specialized: string };
		target_value?: { basic: string; specialized: string };
		delta_percent?: { basic: string; specialized: string };
	};
	target: {
		delta: number;
		grade: string;
		score: number;
		metric_value: number;
		delta_percent: number;
		bottom_line_meeting_target: number;
		bottom_line_per_unit_delta: number;
	};
	metric_factory: string;
	metric_factory_description: string;
	suggestion_formats: string[];
	nudge_factory: string;
	always_suggest: boolean;
};

export type ReportCardSectionDetails = {
	header: string;
	footer: string;
	content: {
		header: string;
		items: string[];
	};
};

export type ReportCardContentSection = {
	id: number;
	name: string;
	grade: string | null;
	score: string | null;
	description: string | null;
	display_order: number;
	target_grade: string | null;
	summary: string[];
	details: ReportCardSectionDetails | null;
	items: ReportCardSectionItem[];
	report_card_factory_group_id: number | null;
};

export type ReportCardContentGroup = {
	id: number;
	name: string;
	description: string | null;
	grade: string | null;
	score: string | null;
	display_order: number;
	report_card_factory_sections: ReportCardContentSection[];
};

export type ReportCardContent = {
	report_card_factory_groups: ReportCardContentGroup[];
	report_card_factory_sections: ReportCardContentSection[];
	ungrouped_report_card_factory_sections: ReportCardContentSection[];
};

// Response from GET /api/customer/report_cards/:slug
export type ReportCardPageData = {
	authorized?: boolean;
	factory_name?: string;
	factory_description?: string | null;
	factory_slug?: string;
	reference_date?: string;
	requested_reference_date?: string;
	resolved_reference_date?: string;
	quarter_label?: string;
	quarter_date_range?: string;
	uuid?: string | null;
	content?: ReportCardContent | null;
	no_data?: boolean;
	demo?: boolean;
	demo_source?: 'account_history' | 'sample' | 'donor_obfuscated';
	/** Set by the server when served via the DataTurk staff subscription bypass. */
	dt_unlocked?: boolean;
};

/**
 * Fetch the current-period report card by factory slug.
 * Passes optional coordinates as query params (reference_date, periodicity, facets).
 */
export function fetchReportCard(
	slug: string,
	params?: Record<string, string>,
): Promise<ReportCardPageData> {
	const qs =
		params && Object.keys(params).length
			? '?' + new URLSearchParams(params).toString()
			: '';
	return apiFetch<ReportCardPageData>(
		`/api/customer/report_cards/${slug}${qs}`,
	);
}

/**
 * Response from the demo candidates endpoint.
 */
export type DemoCandidatesResponse = {
	candidates: { reference_date: string }[];
	has_demo_content: boolean;
};

/**
 * Fetch available demo reference dates for an unpermitted report card.
 * Returns candidates sorted by proximity to ideal offset (6 months back),
 * plus a flag indicating whether fallback demo_content exists.
 */
export async function fetchReportCardDemoCandidates(
	slug: string,
	referenceDate?: string,
): Promise<DemoCandidatesResponse> {
	const params = referenceDate
		? `?reference_date=${encodeURIComponent(referenceDate)}`
		: '';
	return apiFetch<DemoCandidatesResponse>(
		`/api/customer/report_cards/${slug}/demo_candidates${params}`,
	);
}

/**
 * Fetch demo data for an unpermitted report card (offset reference date).
 * Returns the same shape as fetchReportCard but with `demo: true`.
 * Returns null if no data exists for that date.
 */
export async function fetchReportCardDemo(
	slug: string,
	referenceDate?: string,
): Promise<
	(ReportCardPageData & { demo: boolean; demo_source?: string }) | null
> {
	const qs = referenceDate
		? `?reference_date=${encodeURIComponent(referenceDate)}`
		: '';
	const res = await apiFetch<
		ReportCardPageData & {
			demo?: boolean;
			demo_source?: string;
			no_data?: boolean;
		}
	>(`/api/customer/report_cards/${slug}/demo${qs}`);
	if (res.no_data) return null;
	return { ...res, demo: true };
}

/**
 * Fetch available demo reference dates for an unpermitted report.
 */
export async function fetchReportDemoCandidates(
	slug: string,
): Promise<DemoCandidatesResponse> {
	return apiFetch<DemoCandidatesResponse>(
		`/api/customer/reports/${slug}/demo_candidates`,
	);
}

/**
 * Fetch demo data for an unpermitted report (offset reference date).
 * Returns the same shape as fetchReport but with `demo: true`.
 * Returns null if no data exists for that date.
 */
export async function fetchReportDemo(
	slug: string,
	referenceDate: string,
	extraParams?: Record<string, string>,
): Promise<
	(ReportCardReport & { demo: boolean; demo_source?: string }) | null
> {
	const params = new URLSearchParams({
		reference_date: referenceDate,
		...extraParams,
	});
	const res = await apiFetch<
		ReportCardReport & {
			demo?: boolean;
			demo_source?: string;
			no_data?: boolean;
		}
	>(`/api/customer/reports/${slug}/demo?${params.toString()}`);
	if ('no_data' in res && res.no_data) return null;
	return { ...res, demo: true };
}

/**
 * Fetch available demo reference dates for an unpermitted board.
 */
export async function fetchBoardDemoCandidates(
	slug: string,
): Promise<DemoCandidatesResponse> {
	return apiFetch<DemoCandidatesResponse>(
		`/api/customer/boards/${slug}/demo_candidates`,
	);
}

/**
 * Fetch demo data for an unpermitted board.
 * Returns the board data shape with `demo: true`, or null if no data.
 */
export async function fetchBoardDemo(
	slug: string,
	referenceDate?: string,
): Promise<{
	name?: string;
	slug?: string;
	stages: unknown[];
	demo: boolean;
	demo_source?: string;
	sample_context_token?: string;
	demo_reference_date?: string;
} | null> {
	const qs = referenceDate
		? `?reference_date=${encodeURIComponent(referenceDate)}`
		: '';
	const res = await apiFetch<{
		name?: string;
		slug?: string;
		stages?: unknown[];
		demo?: boolean;
		demo_source?: string;
		sample_context_token?: string;
		demo_reference_date?: string;
		no_data?: boolean;
	}>(`/api/customer/boards/${slug}/demo${qs}`);
	if (res.no_data) return null;
	return {
		...res,
		stages: res.stages ?? [],
		demo: true,
	};
}

// ---------------------------------------------------------------------------
// Account Groups
// ---------------------------------------------------------------------------

export type AccountGroupEntry = {
	id: number;
	name: string;
	account_count: number;
};

/**
 * Fetch account groups visible to the current user.
 */
export function fetchAccountGroups(): Promise<AccountGroupEntry[]> {
	return apiFetch<AccountGroupEntry[]>('/api/customer/account_groups');
}
